/**
 * MetaRow — 不符合項管理頁面共享的 meta 行元件。
 *
 * - MetaStat: 帶 tone 配色的「label + 數值」單元
 * - MetaDivider: 視覺上的分隔點（·）
 *
 * 從 4 個 Tab 內容組件（NC 報告 / 觀察 / 追蹤 / 改善）中抽取共用，
 * 避免同一定義散落 4 份。
 */
import { color, font, text, weight } from '@/styles/tokens';

/** MetaStat 的色調（對應 token state 與 text.tertiary） */
export type MetaStatTone = 'info' | 'warning' | 'success' | 'danger' | 'muted';

/** MetaStat props */
interface MetaStatProps {
  /** 左側標籤文字 */
  label: string;
  /** 右側大號數值 */
  value: number;
  /** 數值色調 */
  tone: MetaStatTone;
}

/**
 * 渲染 ListPageShell.meta 行裡的單個統計（label + 大號數值）。
 * 數值顏色根據 tone 從 token 取色。
 */
export function MetaStat({ label, value, tone }: MetaStatProps) {
  const valueColor =
    tone === 'danger'
      ? color.state.danger
      : tone === 'warning'
        ? color.state.warning
        : tone === 'success'
          ? color.state.success
          : tone === 'info'
            ? color.state.info
            : color.text.tertiary;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ color: color.text.muted, fontSize: text.sm }}>{label}</span>
      <span
        style={{
          fontFamily: font.display,
          fontSize: text.lg,
          fontWeight: weight.bold,
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </span>
    </span>
  );
}

/** 分隔點（小圓點 ·），用於 MetaStat 之間視覺分隔 */
export function MetaDivider() {
  return <span style={{ color: color.border.strong }}>·</span>;
}
